import { useMemo } from 'react';
import useProductAvailability from '../../availability/queries/useProductAvailability';
import useUserBranches from '../../core/queries/useUserBranches';
import notEmpty from '../../core/util/notEmpty';
import { useProductPrices } from '../../pricing/queries/useProductPrices';
import type { BaseProductData } from '../types';
import { findBySku } from '../util/findBySku';
import useCurrentCustomer from '../../core/hooks/useCurrentCustomer';

const useProductsWithRelatedData = (products: BaseProductData[]) => {
    const skuList = products
        .flatMap(product => {
            return [
                product.sku,
                ...product.variants.map(variant => variant.sku),
            ];
        })
        .filter(notEmpty);

    const { customerPrices, loading } = useProductPrices({ skuList });

    const { branches } = useUserBranches();

    const { loading: stockLoading } = useProductAvailability(skuList);

    const customer = useCurrentCustomer();
    const { loading: stockLoadingSecond } = useProductAvailability(
        skuList,
        branches.find(branch => branch.id !== customer?.locationCode)?.id
    );

    const productsWithRelatedData = useMemo(() => {
        if (!customerPrices) {
            return undefined;
        }
        return products.map(product => {
            return {
                ...product,
                customerPrice: product.sku
                    ? findBySku(customerPrices, product.sku)
                    : undefined,
                variants: product.variants.map(variant => {
                    return {
                        ...variant,
                        customerPrice: findBySku(customerPrices, variant.sku),
                    };
                }),
            };
        });
    }, [products, customerPrices]);

    return {
        productsWithRelatedData,
        loading: loading || stockLoading || stockLoadingSecond,
    };
};

export default useProductsWithRelatedData;
